import { hBuilding, iMap, iPhone } from '@hover/icons';
import React from 'react';

import { Alert, Button } from 'src/shared/components';
import { Form as SharedForm, TextField } from 'src/shared/components/forms';
import { useUpgradeToProMutation } from 'src/shared/graphql/generated';
import { useClosure, useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { identify } from 'src/shared/telemetry';
import { isNetworkError } from 'src/shared/utils/graphql';
import { baseUrlToRedirectUrl } from 'src/shared/utils/url';

import { makeValidationSchema } from './validationSchema';

interface UpgradeFormData {
  company: string;
  mobilePhone: string;
  zipCode: string;
}

export interface UpgradeFormProps {
  userId: number;
  orgId: number;
}

const UpgradeForm = ({ userId, orgId }: UpgradeFormProps) => {
  const t = useTranslate(TranslationNamespace.SIGN_UP);

  const initialValues: UpgradeFormData = {
    company: '',
    mobilePhone: '',
    zipCode: '',
  };

  const [upgradeToPro, { error, loading }] = useUpgradeToProMutation();

  const onSubmit = useClosure(async (formData: UpgradeFormData) => {
    try {
      const { data } = await upgradeToPro({
        variables: { orgId, userId, ...formData },
      });

      if (data) {
        const {
          user: { id, email, intercomSha },
        } = data.upgradeToPro;

        try {
          // TODO: upgrade analytics call

          await identify({
            userId: id,
            email,
            intercomSha,
          });
        } finally {
          window.location.href = baseUrlToRedirectUrl(window.location.href);
        }
      }
    } catch {
      // TODO: expected authorization errors should probably be
      // handled in an Apollo error link at the client level
    }
  });

  const validationSchema = makeValidationSchema(t);

  const hasErrorAtIdle = error && !loading;
  const hasNetworkError = hasErrorAtIdle && isNetworkError(error);

  return (
    <SharedForm<UpgradeFormData>
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      <TextField
        iconBefore={hBuilding}
        label={t('company')}
        name="company"
        placeholder={t('company')}
      />
      <TextField
        iconBefore={iPhone}
        label={t('mobilePhone')}
        name="mobilePhone"
        placeholder={t('mobilePhone')}
        type="tel"
      />
      <TextField
        iconBefore={iMap}
        label={t('zipCode')}
        name="zipCode"
        placeholder={t('zipCode')}
      />
      {hasErrorAtIdle && (
        <Alert alignSelf="stretch" flexDirection="row" marginTop={300}>
          {hasNetworkError
            ? t('somethingWentWrongTryAgain')
            : t('somethingWentWrongOnOurSide')}
        </Alert>
      )}
      <Button
        alignSelf="stretch"
        isLoading={loading}
        marginTop={500}
        size="large"
        testId="upgrade-account-button"
        type="submit"
      >
        {t('upgrade.upgrade')}
      </Button>
    </SharedForm>
  );
};

export { UpgradeForm };
